import { useState } from "react";
import { Tooltip } from "react-tooltip";
import {
	ArrowPathIcon,
	CheckBadgeIcon as CheckBadgeIconOutline,
	ClockIcon,
	ExclamationTriangleIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { DataTable, DataTableRowClickEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { localeShortDateTime } from "../../../app/datetimeUtils";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import Select from "../../../components/input/Select";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import TextWithTip from "../../../components/TextWithTip";
import { DraftStatus } from "../writingApiSlice";
import { UserDraftHistoryDraft } from "./draftAutosaveHistoryApiSlice";
import DraftAutosaveHistoryModal from "./DraftAutosaveHistoryModal";

interface DraftTableProps {
	drafts: UserDraftHistoryDraft[];
	promptTitle: string;
}

export default function AutosaveHistoryDraftTable({ drafts, promptTitle }: DraftTableProps) {
	const popoverBodyTemplate = () => {
		return (
			<ArrowTopRightOnSquareIcon className="size-5 shrink-0 text-gray-400 group-hover:text-gray-600" />
		);
	};

	const createDateBodyTemplate = (row: UserDraftHistoryDraft) => {
		return <FormattedDateAndTime date={row.createDate} />;
	};

	const lastUpdateDateBodyTemplate = (row: UserDraftHistoryDraft) => {
		return <FormattedDateAndTime date={row.lastUpdateDate} />;
	};

	const titleBodyTemplate = (row: UserDraftHistoryDraft) => {
		return <TextWithTip uniqueName={`title-${row.id}`} text={row.draftTitle} />;
	};

	const statusBodyTemplate = (row: UserDraftHistoryDraft) => {
		return (
			<div className="flex items-center">
				{row.draftStatus === DraftStatus.Finalized ? (
					<CheckBadgeIcon
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.draftStatus === DraftStatus.Scored ? (
					<CheckBadgeIconOutline
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.draftStatus === DraftStatus.Revised ? (
					<ArrowPathIcon
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="revised-tooltip"
					/>
				) : row.draftStatus === DraftStatus.ScoringFailed ||
				  row.draftStatus === DraftStatus.Incomplete ? (
					<ClockIcon
						className="size-5 text-yellow-500"
						aria-hidden="true"
						data-tooltip-id="in-progress-tooltip"
					/>
				) : row.draftStatus === DraftStatus.Deleted ? (
					<TrashIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="deleted-tooltip"
					/>
				) : (
					<ExclamationTriangleIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="not-started-tooltip"
					/>
				)}
				<span className="ml-2">{row.draftStatusDescription}</span>
				<Tooltip id="completed-tooltip" place="top" content="Completed" />
				<Tooltip id="revised-tooltip" place="top" content="Revised" />
				<Tooltip id="in-progress-tooltip" place="top" content="In Progress" />
				<Tooltip id="not-started-tooltip" place="top" content="Not Started" />
				<Tooltip id="deleted-tooltip" place="top" content="Deleted" />
			</div>
		);
	};

	const handleRowClick = (event: DataTableRowClickEvent) => {
		setDialogOpen(true);
		setSelectedDraftId(event.data.id);
		setSelectedDraftDate(localeShortDateTime(event.data.createDate));
	};

	// Dialog
	const [dialogOpen, setDialogOpen] = useState<boolean>(false);
	const [selectedDraftId, setSelectedDraftId] = useState<number>();
	const [selectedDraftDate, setSelectedDraftDate] = useState<string>();

	const resetDialog = () => {
		setDialogOpen(false);
		setSelectedDraftId(undefined);
		setSelectedDraftDate(undefined);
	};

	// Filtering
	const [selectedStatus, setSelectedStatus] = useState<string>();

	const statusOptions = Array.from(
		new Set(drafts.map((draft) => draft.draftStatusDescription).filter(Boolean))
	);

	// Filter drafts by status
	const filteredDrafts = drafts.filter((draft) => {
		return (
			!selectedStatus ||
			selectedStatus === "All" ||
			draft.draftStatusDescription === selectedStatus
		);
	});

	return (
		<section>
			{drafts && (
				<div className="-mx-4 -my-3 px-4 pb-3 bg-gray-100">
					<Select
						value={selectedStatus}
						setValue={(v) => setSelectedStatus(v as string)}
						label="Status"
						simpleListBoxId="statusFilter"
						options={[
							{ value: "All", label: "All" },
							...statusOptions.map((status) => ({
								value: status,
								label: status,
							})),
						]}
						fieldClasses="pt-1"
						simpleListBoxClasses="w-64"
					/>
					<DataTable
						value={filteredDrafts}
						dataKey="id"
						stripedRows
						sortIcon={CustomSortIcon}
						emptyMessage="No results found"
						className="tbl !w-auto mt-2 mx-2 xl:mx-8"
						selectionMode="single"
						onRowClick={handleRowClick}
						rowClassName={() => "group cursor-pointer"}>
						<Column
							body={popoverBodyTemplate}
							headerClassName="!pl-3 !pr-0.5 w-8"
							className="!pl-3 !pr-0.5 w-8"
						/>
						<Column
							header="Create Date"
							body={createDateBodyTemplate}
							sortable
							sortField="createDate"
						/>
						<Column
							header="Last Update Date"
							body={lastUpdateDateBodyTemplate}
							sortable
							sortField="lastUpdateDate"
						/>
						<Column
							header="Title"
							body={titleBodyTemplate}
							sortable
							sortField="draftTitle"
						/>
						<Column
							header="Status"
							body={statusBodyTemplate}
							sortable
							sortField="draftStatusDescription"
						/>
					</DataTable>
				</div>
			)}
			<DraftAutosaveHistoryModal
				draftId={selectedDraftId}
				promptTitle={promptTitle}
				draftDateTime={selectedDraftDate}
				dialogOpen={dialogOpen}
				resetDialog={resetDialog}
			/>
		</section>
	);
}
